import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import Link from '@components/shared/Link';
import { getPagePath, getParentId } from '@/utils/pagePaths';
import { useAppContext } from '@/context/AppContext';
import { useSlideshowExperience } from '@components/shared/ImageViewer/hooks';
import SlideshowText from '@components/page/SlideshowText';
import { SpreadViewer } from '@components/shared/ImageViewer';
import { inlineEditingEnabled, useEditorFrame } from '@components/preview/editGates';
import { SiteLogoMark } from '@components/shared/FilteredMark';
import { SLIDESHOW_ADD_IMAGES_MESSAGE } from '@/appearance/inspect/messages';
import { ENABLE_VIEWER_PAGE_FADES } from '@components/shared/ImageViewer/imageViewerUtils';
import { slideshowChromeFor } from '@/slideshow/settings';
import { chromeCornerClass } from '@/slideshow/chrome';
// Kept for ENABLE_VIEWER_PAGE_FADES; unused while fades are off.
const THEME_SETTLE_MS = 250;

/**
 * Editorial Slideshow Component
 *
 * Where a slideshow page opens:
 * - Desktop: redirects to the first slide, with the text in its side panel or corner
 * - Mobile: renders the viewer directly, with the text as a swipeable slide (no redirect)
 *
 * The actual viewing experience is handled by SpreadViewer.
 */
function EditorialSlideshow({ url, images, history: historyProp, content: pageContent, page = null }) {
    const navigate = useNavigate();
    const history = historyProp ?? {
        push: (path) => navigate(path),
        replace: (path) => navigate(path, { replace: true }),
    };
    const { content, theme } = useAppContext();
    const experience = useSlideshowExperience();
    const screenShape = experience.shape;
    const hasRedirected = useRef(false);
    const [inEditor, setInEditor] = useState(false);
    const inEditorFrame = useEditorFrame();

    useEffect(() => {
        setInEditor(inlineEditingEnabled());
    }, []);

    // Get the first image, handling spreads (which are arrays)
    const firstImage = useMemo(() => {
        if (!images || images.length === 0 || !content?.files) return null;
        const firstItem = images[0];

        // Check if first item is a spread (array of 2 items)
        if (Array.isArray(firstItem) && firstItem.length === 2) {
            const spreadImg = firstItem[0];
            const imgId = typeof spreadImg === 'string' ? spreadImg : spreadImg.id;
            return content.files[imgId] || null;
        }

        const imgId = typeof firstItem === 'string' ? firstItem : firstItem.id;
        return content.files[imgId] || null;
    }, [images, content]);

    const files = content?.files || {};
    const pagesObj = content?.pages || {};
    const pagesForHtml = pagesObj;

    const pageDataObj = useMemo(() => {
        if (!url || !pagesObj) return null;
        return Object.values(pagesObj).find((p) => {
            const pagePath = getPagePath(p, pagesObj);
            return pagePath === url;
        });
    }, [url, pagesObj]);

    const closeUrl = useMemo(() => {
        if (!pageDataObj) return '/';
        const parentId = getParentId(pageDataObj);
        if (parentId && pagesObj[parentId]) {
            const parentPage = pagesObj[parentId];
            const parentPath = getPagePath(parentPage, pagesObj);
            return parentPath ? `/${parentPath}` : '/';
        }
        return '/';
    }, [pageDataObj, pagesObj]);

    // The home slideshow has no page entry; its text lives on the home object.
    const textPage = useMemo(() => {
        const source = page || pageDataObj || {};
        return pageContent === undefined ? source : { ...source, content: pageContent };
    }, [page, pageDataObj, pageContent]);
    const chromeCorners = slideshowChromeFor(textPage.slideshow, { phone: experience.phone });

    const isBigWide = screenShape === 'big-wide';
    const isBigTall = screenShape === 'big-tall';
    const isMobile = screenShape === 'small';

    const isPhone = experience.phone;
    const isTouchDevice = experience.touch;
    const isDeviceDetected = experience.detected;
    const isLargeTouchDevice = isTouchDevice && !isPhone && (isBigWide || isBigTall);
    const isPhoneDevice = isTouchDevice && isPhone;
    const isSingleImageFallbackMode = isBigTall && experience.phoneLayout;

    // Redirect to first image on mount (desktop non-touch only).
    useEffect(() => {
        if (hasRedirected.current) return;
        if (!firstImage) return;
        if (typeof window === 'undefined') return;
        if (!isDeviceDetected) return;
        if (isMobile) return;
        if (isPhoneDevice) return;
        if (isLargeTouchDevice) return;
        if (isSingleImageFallbackMode) return;

        const targetUrl = `/${url}/${firstImage.id}.img`;
        hasRedirected.current = true;
        requestAnimationFrame(() => {
            history.replace(targetUrl);
        });
    }, [firstImage, url, history, isMobile, isPhoneDevice, isLargeTouchDevice, isSingleImageFallbackMode, isDeviceDetected]);

    const renderSpread = (startAtText = true) => {
        if (!firstImage) return null;
        const spreadViewer = (
            <SpreadViewer
                history={history}
                startAtTextSlide={startAtText}
                overrideSlug={url}
                overrideImageId={firstImage.id}
                contentAppearDelayMs={ENABLE_VIEWER_PAGE_FADES && isSingleImageFallbackMode && !inEditor ? THEME_SETTLE_MS : 0}
            />
        );
        if (typeof document !== 'undefined') {
            return ReactDOM.createPortal(spreadViewer, document.body);
        }
        return spreadViewer;
    };

    if ((isMobile || isPhoneDevice || isLargeTouchDevice || isSingleImageFallbackMode) && firstImage) {
        return renderSpread(true);
    }

    if (firstImage) {
        return null;
    }

    // No images yet: the slideshow still owns the screen, with its text across it.
    return (
        <div data-page-path="page.frame" className="fixed inset-0 z-[9999] flex flex-col bg-background">
            <SlideshowChrome closeUrl={closeUrl} logo={theme?.brand?.logo} corners={chromeCorners} />
            <div className="flex-1 overflow-y-auto px-[1.6em] pt-[calc(22px+3.36em+2rem)] pb-8">
                <SlideshowText
                    placement="empty"
                    page={textPage}
                    pages={pagesForHtml}
                    files={files}
                    theme={theme}
                />
                {inEditorFrame && (
                    <button
                        type="button"
                        className="mt-10 rounded border border-dashed border-current px-4 py-3 text-sm opacity-60 transition-opacity hover:opacity-100"
                        style={{ color: 'var(--color-text)' }}
                        onClick={() => window.parent.postMessage({ type: SLIDESHOW_ADD_IMAGES_MESSAGE }, window.location.origin)}
                    >
                        Add images
                    </button>
                )}
            </div>
        </div>
    );
}

function SlideshowChrome({ closeUrl, logo, corners }) {
    return (
        <>
            {corners.logo !== 'hidden' && (
                <Link
                    aria-label="Home"
                    data-slideshow-logo=""
                    className={`absolute ${chromeCornerClass('logo', corners.logo)} z-30 inline-block h-[3.36em] w-[160px] max-w-full opacity-100 transition-opacity duration-200 hover:opacity-80`}
                    to={closeUrl}
                >
                    <SiteLogoMark
                        logo={logo}
                        context="slideshow"
                        className="pointer-events-none absolute inset-0"
                    />
                </Link>
            )}

            {corners.close !== 'hidden' && (
                <Link
                    data-slideshow-close=""
                    className={`absolute ${chromeCornerClass('close', corners.close)} z-20 flex items-center justify-center p-5 min-w-[44px] min-h-[44px] hover:no-underline transition-colors duration-200`}
                    style={{ color: 'var(--color-text)' }}
                    to={closeUrl}
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </Link>
            )}
        </>
    );
}

SlideshowChrome.propTypes = {
    closeUrl: PropTypes.string.isRequired,
    logo: PropTypes.any,
    corners: PropTypes.shape({ logo: PropTypes.string, close: PropTypes.string }).isRequired,
};

EditorialSlideshow.propTypes = {
    url: PropTypes.string,
    images: PropTypes.array,
    history: PropTypes.object,
    content: PropTypes.string,
    page: PropTypes.object,
};

export default EditorialSlideshow;
